import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  type DataTableLoadingObject,
  LongText,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';

import { type APP_NAME } from '../../../../consts';
import { type AssortmentSkuLocation } from '../../../../redux/assortment';

const COLUMN_ID = 'location' as const;

export type LocationColumnId = typeof COLUMN_ID;

interface LocationFields {
  location: AssortmentSkuLocation['location'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<LocationFields>();

export const createLocationColumn = <
  T extends LocationFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.location.id + row.location.desc),
    {
      id: COLUMN_ID,
      size: 200,
      header: function LocationHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        return (
          <DataTableHeaderCellWithSorting
            column={column}
            arrowPosition="right"
            showSortable
          >
            <DataTableCellContent>
              <DataTableCellTitle className="uppercase font-medium">
                {t('table-columns.location')}
              </DataTableCellTitle>
            </DataTableCellContent>
          </DataTableHeaderCellWithSorting>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function LocationCell({ row }) {
            const { location } = row.original;

            return (
              <DataTableCellContent>
                <DataTableCellTitle>
                  <LongText value={location.desc} maxLength={30} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
